import '@xyflow/react/dist/base.css';
import type { AgentDto, TaskDto } from '@af/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from '@xyflow/react';
import { memo, type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentSectionLinks } from '@/components/agent/AgentSectionLinks';
import { AgentNow } from '@/components/dashboard/AgentHoverCard';
import { AgentsLiveList, LIVE_ORDER, LiveStatusLabel } from '@/components/dashboard/AgentsLive';
import { OfficeView } from '@/components/dashboard/OfficeView';
import { AgentOrb } from '@/components/ui/agent-orb';
import { buttonVariants, Spinner } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { IconList, IconOffice, IconPlus } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { ctaCls } from '@/components/ui/link-styles';
import { Empty, ErrorBox } from '@/components/ui/misc';
import { StatusDot } from '@/components/ui/status-dot';
import { AgentSelectContext, useAgentSelect } from '@/lib/agent-select';
import { appLocale, relTime, truncate, usd } from '@/lib/format';
import { type AgentLive, type LiveStatus, useAgentsLive } from '@/lib/live-agents';
import { activeDelegations, type DelegationLine, delegationByPair } from '@/lib/org-activity';
import { layoutTree } from '@/lib/org-layout';
import { Can } from '@/lib/permissions';
import { useAgents, useConversations, useRuns, useTasks } from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import { useIsPhone } from '@/lib/use-media';
import { cn, safeStorage } from '@/lib/utils';
import { CompanyActivity } from './CompanyActivity';

type AgentNodeData = {
  agent: AgentDto;
  currentTask: string | null;
  /** Name of the agent that handed this one work that is still going on. */
  delegatedBy: string | null;
};
type AgentNodeT = Node<AgentNodeData, 'agent'>;

function agentTone(a: AgentDto, currentTask: string | null) {
  if (a.status === 'paused' || a.status === 'archived') return 'paused' as const;
  if (currentTask?.startsWith('⚑')) return 'approval' as const;
  if ((a.stats?.activeRuns ?? 0) > 0) return 'running' as const;
  return 'idle' as const;
}

const AgentNode = memo(function AgentNode({ data }: NodeProps<AgentNodeT>) {
  const { agent, currentTask, delegatedBy } = data;
  const tone = agentTone(agent, currentTask);
  const working = tone === 'running' || tone === 'approval';
  return (
    <div
      className={cn(
        'w-[220px] rounded-xl border bg-panel text-left transition-colors hover:border-accent',
        working ? 'border-claude/60 shadow-soft' : 'border-border-strong',
      )}
    >
      <Handle type="target" position={Position.Top} className="!h-1.5 !w-1.5 !border-0 !bg-faint" />
      <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
        <span
          aria-hidden
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-panel-2 font-mono text-claude"
        >
          {agent.avatar}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{agent.name}</div>
          <div className="truncate text-xs text-muted">{agent.roleTitle}</div>
        </div>
        <StatusDot tone={tone} label={tone} />
      </div>
      <div className="px-2.5 py-1.5 font-mono text-[11px]">
        {delegatedBy ? <div className="truncate text-claude">↳ for {delegatedBy}</div> : null}
        <div className="truncate text-muted" title={currentTask ?? ''}>
          ⎿ {currentTask ? truncate(currentTask, 34) : agent.status === 'paused' ? 'paused' : 'idle'}
        </div>
        <div className="mt-0.5 flex justify-between text-muted tabular">
          <span>{agent.stats?.activeRuns ?? 0} active</span>
          <span className="text-fg">{usd(agent.stats?.costTodayUsd ?? 0, 2)} today</span>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!h-1.5 !w-1.5 !border-0 !bg-faint" />
    </div>
  );
});

const nodeTypes = { agent: AgentNode };

export function currentTaskFor(agentId: string, tasks: TaskDto[]): string | null {
  const t =
    tasks.find((x) => x.assigneeAgentId === agentId && x.status === 'awaiting_approval') ??
    tasks.find(
      (x) => x.assigneeAgentId === agentId && ['running', 'queued', 'waiting_children'].includes(x.status),
    );
  if (!t) return null;
  return t.status === 'awaiting_approval' ? `⚑ ${t.title}` : t.title;
}

/** Colour of a live delegation line. */
const LINE_COLOR = { running: 'var(--claude)', approval: 'var(--warn)', waiting: 'var(--faint)' } as const;

/**
 * Phone: the tree as an indented list with connector lines. A line under an agent says whom it is
 * working for or whom it handed work to right now (the same reporting lines the chart animates).
 */
function OrgList({
  agents,
  tasks,
  lines,
}: {
  agents: AgentDto[];
  tasks: TaskDto[];
  lines: DelegationLine[];
}) {
  const ids = new Set(agents.map((a) => a.id));
  const nameOf = new Map(agents.map((a) => [a.id, a.name]));
  const children = (id: string | null) =>
    agents.filter((a) =>
      id === null ? !a.managerAgentId || !ids.has(a.managerAgentId) : a.managerAgentId === id,
    );
  const render = (a: AgentDto, seen: Set<string>): ReactNode => {
    if (seen.has(a.id)) return null;
    const next = new Set(seen).add(a.id);
    const task = currentTaskFor(a.id, tasks);
    const tone = agentTone(a, task);
    const outgoing = lines.filter((l) => l.from === a.id);
    const incoming = lines.find((l) => l.to === a.id);
    const kids = children(a.id);
    return (
      <li key={a.id} className="relative">
        <Link
          to="/agents/$id"
          params={{ id: a.id }}
          className={cn(
            'flex items-center gap-2.5 rounded-xl border bg-panel px-3 py-2.5 hover:border-accent',
            tone === 'running' || tone === 'approval' ? 'border-claude/60' : 'border-border',
          )}
        >
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-panel-2 font-mono text-claude"
          >
            {a.avatar}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{a.name}</span>
            <span className="block truncate text-xs text-muted">
              {task ? `⎿ ${truncate(task, 40)}` : a.roleTitle}
            </span>
            {incoming ? (
              <span className="block truncate text-xs text-claude">
                ↳ for {nameOf.get(incoming.from) ?? 'a colleague'}
              </span>
            ) : null}
            {outgoing.map((l) => (
              <span key={l.taskId} className="block truncate text-xs text-claude">
                → {nameOf.get(l.to) ?? 'a colleague'}: {truncate(l.title, 28)}
              </span>
            ))}
          </span>
          <span className="shrink-0 text-right font-mono text-[11px] text-muted tabular">
            <span className="block">{usd(a.stats?.costTodayUsd ?? 0, 2)}</span>
            <StatusDot tone={tone} label={tone} />
          </span>
        </Link>
        {kids.length ? (
          <ul className="mt-2 ml-4 flex flex-col gap-2 border-l-2 border-border-strong pl-3">
            {kids.map((c) => render(c, next))}
          </ul>
        ) : null}
      </li>
    );
  };
  return (
    <ul aria-label="Organization chart" className="flex flex-col gap-2 overflow-y-auto p-3">
      {children(null).map((a) => render(a, new Set()))}
    </ul>
  );
}

/** Org chart (live via /api/events → query invalidation). Active delegation lines are drawn on top of the hierarchy. */
export function OrgChart() {
  const { t } = useTranslation();
  const phone = useIsPhone();
  const agents = useAgents();
  const tasks = useTasks();
  const navigate = useNavigate();
  const select = useAgentSelect();
  const { theme } = useTheme();

  const live = useMemo(() => (agents.data ?? []).filter((a) => a.status !== 'archived'), [agents.data]);
  const lines = useMemo(() => activeDelegations(tasks.data ?? []), [tasks.data]);

  const { nodes, edges } = useMemo(() => {
    const ids = new Set(live.map((a) => a.id));
    const nameOf = new Map(live.map((a) => [a.id, a.name]));
    const pairs = delegationByPair(lines.filter((l) => ids.has(l.from) && ids.has(l.to)));
    const pos = layoutTree(live.map((a) => ({ id: a.id, managerId: a.managerAgentId })));
    const nodes: AgentNodeT[] = live.map((a) => {
      const incoming = lines.find((l) => l.to === a.id);
      return {
        id: a.id,
        type: 'agent',
        position: pos.get(a.id) ?? { x: 0, y: 0 },
        data: {
          agent: a,
          currentTask: currentTaskFor(a.id, tasks.data ?? []),
          delegatedBy: incoming ? (nameOf.get(incoming.from) ?? null) : null,
        },
        draggable: true,
      };
    });
    const edges: Edge[] = [];
    const drawn = new Set<string>();
    for (const a of live) {
      if (!a.managerAgentId || !ids.has(a.managerAgentId)) continue;
      const key = `${a.managerAgentId}->${a.id}`;
      const line = pairs.get(key);
      drawn.add(key);
      edges.push({
        id: key,
        source: a.managerAgentId,
        target: a.id,
        type: 'smoothstep',
        animated: line?.state === 'running',
        label: line ? (line.count > 1 ? `${line.count} tasks` : truncate(line.title, 24)) : undefined,
        labelStyle: { fill: 'var(--fg-2)', fontSize: 11 },
        labelBgStyle: { fill: 'var(--panel)' },
        style: {
          stroke: line ? LINE_COLOR[line.state] : 'var(--border-strong)',
          strokeWidth: line ? 2.5 : 1.5,
          strokeDasharray: line?.state === 'waiting' ? '5 4' : undefined,
        },
      });
    }
    // Delegation to someone who is not a direct report: still an active line, drawn dashed on top.
    for (const [key, line] of pairs) {
      if (drawn.has(key)) continue;
      edges.push({
        id: `deleg-${key}`,
        source: line.from,
        target: line.to,
        type: 'smoothstep',
        animated: line.state === 'running',
        label: line.count > 1 ? `${line.count} tasks` : truncate(line.title, 24),
        labelStyle: { fill: 'var(--fg-2)', fontSize: 11 },
        labelBgStyle: { fill: 'var(--panel)' },
        style: { stroke: LINE_COLOR[line.state], strokeWidth: 2.5, strokeDasharray: '2 5' },
      });
    }
    return { nodes, edges };
  }, [live, lines, tasks.data]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ErrorBox error={agents.error} />
      <div className="relative min-h-[360px] flex-1 bg-bg">
        {agents.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : nodes.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <Empty>{t('ops.empty')}</Empty>
            <Can perm="agent:create">
              <Link to="/agents/new" className={ctaCls}>
                {t('ops.emptyCta')}
              </Link>
            </Can>
          </div>
        ) : phone ? (
          <OrgList agents={live} tasks={tasks.data ?? []} lines={lines} />
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.3}
            proOptions={{ hideAttribution: true }}
            nodesConnectable={false}
            onNodeClick={(_, n) =>
              select ? select(n.id) : void navigate({ to: '/agents/$id', params: { id: n.id } })
            }
            colorMode={theme}
            aria-label="Organization chart"
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="var(--border)" />
            <Controls showInteractive={false} />
          </ReactFlow>
        )}
      </div>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border px-3 py-1.5 text-xs text-muted">
        <span className="inline-flex items-center gap-1.5">
          <StatusDot tone="running" /> {t('company.legendRunning')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <StatusDot tone="approval" /> {t('company.legendApproval')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <StatusDot tone="idle" /> {t('company.legendIdle')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <StatusDot tone="paused" /> {t('company.legendPaused')}
        </span>
        <span className="text-faint">
          · {t('company.activeLines', { count: new Set(lines.map((l) => `${l.from}${l.to}`)).size })} ·{' '}
          {phone ? t('company.tapHint') : t('company.clickHint')}
        </span>
      </p>
    </div>
  );
}

const STATUS_CHIPS: LiveStatus[] = ['running', 'approval', 'failed', 'idle', 'paused'];
const TAB_KEY = 'af:company-tab';
const VIEW_KEY = 'af:company-view';

/** Live operations: status strip, filters and the list or the office floor. */
function LiveOperations() {
  const { t } = useTranslation();
  const live = useAgentsLive({ tails: true });
  const [q, setQ] = useState('');
  const [only, setOnly] = useState<LiveStatus | null>(null);
  const [view, setView] = useState<'list' | 'office'>(() =>
    safeStorage.get<string>(VIEW_KEY) === 'office' ? 'office' : 'list',
  );
  const count = (s: LiveStatus) => live.list.filter((l) => l.status === s).length;
  const filtered: AgentLive[] = useMemo(() => {
    const needle = q.toLowerCase();
    return live.list
      .filter((l) => !only || l.status === only)
      .filter((l) =>
        `${l.agent.name} ${l.agent.roleTitle} ${l.agent.slug} ${l.agent.currentVersion?.modelRef ?? ''}`
          .toLowerCase()
          .includes(needle),
      )
      .sort(
        (a, b) =>
          LIVE_ORDER[a.status] - LIVE_ORDER[b.status] ||
          a.agent.name.localeCompare(b.agent.name, appLocale()),
      );
  }, [live.list, q, only]);

  const pick = (v: 'list' | 'office') => {
    setView(v);
    safeStorage.set(VIEW_KEY, v);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
      <ErrorBox error={live.error} />
      {live.isPending ? (
        <Spinner />
      ) : live.list.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <Empty>{t('ops.empty')}</Empty>
          <Can perm="agent:create">
            <Link to="/agents/new" className={ctaCls}>
              {t('ops.emptyCta')}
            </Link>
          </Can>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <fieldset className="flex flex-wrap gap-1.5">
              <legend className="sr-only">{t('company.filterStatus')}</legend>
              {STATUS_CHIPS.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={only === s}
                  onClick={() => setOnly(only === s ? null : s)}
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors',
                    only === s
                      ? 'border-accent bg-accent/10 text-fg'
                      : 'border-border text-muted hover:text-fg',
                  )}
                >
                  {t(`ops.status.${s}`)} <span className="tabular-nums">{count(s)}</span>
                </button>
              ))}
            </fieldset>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-36">
                <Input
                  prompt
                  aria-label={t('ops.filter')}
                  placeholder={`${t('common.search')}…`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <fieldset className="inline-flex rounded-lg border border-border p-0.5">
                <legend className="sr-only">{t('ops.view')}</legend>
                {(['list', 'office'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    onClick={() => pick(v)}
                    className={cn(
                      'flex h-7 items-center gap-1.5 rounded-md px-2.5 text-sm',
                      view === v ? 'bg-panel-2 font-medium text-fg shadow-soft' : 'text-muted hover:text-fg',
                    )}
                  >
                    {v === 'list' ? <IconList size={14} /> : <IconOffice size={14} />}
                    {t(`ops.${v}`)}
                  </button>
                ))}
              </fieldset>
              <Can perm="agent:create">
                <Link to="/agents/new" className={buttonVariants('default')}>
                  <IconPlus size={14} /> {t('nav.newAgent')}
                </Link>
              </Can>
            </div>
          </div>
          {filtered.length === 0 ? (
            <Empty>{t('common.noData')}</Empty>
          ) : view === 'office' ? (
            <OfficeView list={filtered} />
          ) : (
            <AgentsLiveList list={filtered} />
          )}
        </>
      )}
    </div>
  );
}

/** Company pane: live operations first, the org chart (with active reporting lines) next to it. */
export function CompanyHub() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'live' | 'org' | 'activity'>(() => {
    const saved = safeStorage.get<string>(TAB_KEY);
    return saved === 'org' || saved === 'activity' ? saved : 'live';
  });
  const choose = (v: 'live' | 'org' | 'activity') => {
    setTab(v);
    safeStorage.set(TAB_KEY, v);
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        role="tablist"
        aria-label={t('company.tabs')}
        className="flex gap-1 border-b border-border px-3 pt-2"
      >
        {(['live', 'org', 'activity'] as const).map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={tab === v}
            onClick={() => choose(v)}
            className={cn(
              '-mb-px h-9 border-b-2 px-3 text-sm transition-colors',
              tab === v ? 'border-accent font-medium text-fg' : 'border-transparent text-muted hover:text-fg',
            )}
          >
            {v === 'activity' ? t('companyActivity.title') : t(`company.tab.${v}`)}
          </button>
        ))}
      </div>
      {tab === 'live' ? <LiveOperations /> : tab === 'activity' ? <CompanyActivity /> : <OrgChart />}
    </div>
  );
}

/** Agent side panel on the Company page: status, live run, recent runs and chats, quick actions. */
function AgentPanel({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const live = useAgentsLive({ tails: true });
  const runs = useRuns({ agentId });
  const convs = useConversations(agentId);
  const l = live.list.find((x) => x.agent.id === agentId);
  if (!l) return null;
  const a = l.agent;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={
          <span className="flex flex-wrap items-center gap-2">
            <AgentOrb seed={a.id} size={26} avatar={a.avatar} name={a.name} />
            {a.name}
            <span className="text-sm font-normal text-muted">{a.roleTitle}</span>
            <Link
              to="/agents/$id"
              params={{ id: a.id }}
              search={{ tab: 'overview' }}
              className={cn(buttonVariants('ghost'), 'ml-auto shrink-0')}
            >
              {t('companyPage.openAgent')}
            </Link>
          </span>
        }
        description={<LiveStatusLabel status={l.status} />}
        footer={
          <>
            <Link to="/tasks" search={{ new: true }} className={buttonVariants('default')}>
              {t('shell.newTask')}
            </Link>
            <Link to="/chat" search={{ new: true, agent: a.id }} className={buttonVariants('primary')}>
              {t('companyPage.chat')}
            </Link>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <AgentSectionLinks agentId={a.id} />
          {l.run && (l.status === 'running' || l.status === 'approval') ? (
            <section>
              <h3 className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
                {t('companyPage.nowWorking')}
              </h3>
              <AgentNow live={l} header={false} />
            </section>
          ) : null}
          <section>
            <h3 className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
              {t('companyPage.recentRuns')}
            </h3>
            <ul className="flex flex-col">
              {(runs.data ?? []).slice(0, 5).map((r) => (
                <li key={r.id}>
                  <Link
                    to="/runs/$id"
                    params={{ id: r.id }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-hover"
                  >
                    <StatusDot status={r.status} />
                    <span className="min-w-0 flex-1 truncate">{r.mode}</span>
                    <span className="text-xs text-muted">{relTime(r.createdAt)}</span>
                  </Link>
                </li>
              ))}
              {runs.data?.length === 0 ? <li className="px-2 text-sm text-muted">—</li> : null}
            </ul>
          </section>
          <section>
            <h3 className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
              {t('companyPage.recentChats')}
            </h3>
            <ul className="flex flex-col">
              {(convs.data ?? []).slice(0, 5).map((c) => (
                <li key={c.id}>
                  <Link
                    to="/chat/$conversationId"
                    params={{ conversationId: c.id }}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-hover"
                  >
                    <span className="min-w-0 flex-1 truncate">{c.title}</span>
                    <span className="text-xs text-muted">{relTime(c.updatedAt)}</span>
                  </Link>
                </li>
              ))}
              {convs.data?.length === 0 ? <li className="px-2 text-sm text-muted">—</li> : null}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The company at a glance, in the middle of the chat page while no conversation is open: live list or office
 * floor, and the org chart with active reporting lines. Talking happens in the chat sidebar next to it; a
 * click on an agent opens its panel instead of leaving the page.
 */
export function CompanyOverview({ onSelectAgent }: { onSelectAgent?: (id: string) => void }) {
  const [agent, setAgent] = useState<string | null>(null);
  return (
    <AgentSelectContext.Provider value={onSelectAgent ?? setAgent}>
      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-panel">
        <CompanyHub />
      </section>
      {agent ? <AgentPanel key={agent} agentId={agent} onClose={() => setAgent(null)} /> : null}
    </AgentSelectContext.Provider>
  );
}
